import { Button, Empty, Tag } from 'antd'
import { useLibraDocument } from 'features/libra/document/LibraDocumentContext'
import { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { useGongAgentAutofill } from 'features/libra/stepComponents/GongAgentAutofill'
import {
  buildDraftZones,
  getZoneAgentPermissions,
  parseZoneProposals,
  ZONE_ANALYSIS_SECTION_ID,
  ZONE_COLORS,
} from 'features/libra/stepComponents/zoneAnalysis/aiSuggestedZones'
import { ZoneParams } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { useZoneAnalysisData } from 'features/libra/stepComponents/zoneAnalysis/ZoneAnalysisDataProvider'
import { useGeckoNotifications } from 'features/notifications'
import { PermissionsGate } from 'features/permissions'
import { useRolodexEntries } from 'features/rolodex'
import isEqual from 'lodash/isEqual'
import { Crop, Plus, Sparkles } from 'lucide-react'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import {
  PERMISSION_GECKO_INTERNAL_MEMBER,
  PERMISSION_MODUS,
  RolodexEntryType,
  RolodexPermissionsValues,
} from 'types'
import { v4 } from 'uuid'

import { CropDocumentPanel } from './CropDocumentPanel'
import { ZoneRow as ZoneRowComponent } from './ZoneRow'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const TopControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  /* Let the action buttons drop to their own row when the panel is narrow —
     without this, flex items (min-width: auto) can't shrink below their
     content and the buttons overflow past the panel edge. */
  flex-wrap: wrap;
`

const LeftControls = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex: 1;
  min-width: 0; /* allow the label/tag to shrink + ellipsize */
`

const Label = styled.span`
  display: flex;
  align-items: center;
  min-width: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-weight: 500;

  /* Long reference-image names ellipsize instead of forcing overflow */
  .ant-tag {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const RightControls = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  flex-shrink: 0; /* buttons keep their natural size; they wrap instead */
`

const ZonesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ZonesHeader = styled.h4`
  margin: 0 0 12px 0;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  background: ${({ theme }) => theme.forecast.background2};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.forecast.border};
`

const EmptyStateTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.defaultText};
  margin-bottom: 8px;
`

const EmptyStateDescription = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.forecast.secondaryText};
  text-align: center;
  max-width: 400px;
  margin-bottom: 16px;
`

interface DrawingBasedZoneEditorProps {
  unitId: string
}

export const DrawingBasedZoneEditor: FC<DrawingBasedZoneEditorProps> = ({
  unitId,
}) => {
  const { zones, setZones, config } = useZoneAnalysisData()
  // Extra AI-gating permissions the workflow author scoped to this section,
  // applied on top of the base Gecko-internal gate — same paradigm as every
  // other agent step (AiAutofillBanner), instead of hardcoding zones' own set.
  const zoneAgentPermissions = useMemo(
    () => getZoneAgentPermissions(config) as RolodexPermissionsValues[],
    [config]
  )
  const { selectedDocumentId, setSelectedDocumentId, setZoneDrawingMode } =
    useLibraDocument()
  const { successNotification, errorNotification, infoNotification } =
    useGeckoNotifications()

  const [isCroppingMode, setIsCroppingMode] = useState(false)
  const [isDrawingMode, setIsDrawingMode] = useState(false)
  const [selectedZoneId, setSelectedZoneId] = useState<string>()
  const [redrawingZoneId, setRedrawingZoneId] = useState<string>()
  // Browser-measured natural size of the reference image currently shown in
  // the document pane (reported by SmartImageViewer via zoneDrawingMode).
  // The single source of truth for converting the AI's normalized bounds
  // into pixel drawing_bounds — the backend never reports image sizes.
  const [imageNaturalSize, setImageNaturalSize] = useState<{
    documentId: string
    width: number
    height: number
  }>()

  // Load ALL documents for this unit
  const { reports: allDocuments, forceRefresh } = useRolodexEntries(
    useMemo(
      () => ({
        type: RolodexEntryType.REPORT,
        tags: { unit: unitId },
      }),
      [unitId]
    ),
    { swrOptions: { revalidateOnFocus: false, compare: isEqual } }
  )

  // Get the currently selected document from sidebar
  const selectedDocument = useMemo(
    () => allDocuments.find((doc) => doc.id === selectedDocumentId),
    [allDocuments, selectedDocumentId]
  )

  const { zoneReferenceImages, isSelectedDocZoneRef } = useMemo(() => {
    const zoneRefs = allDocuments.filter((doc) => {
      const categories = doc.raw.data.categories as string[] | undefined
      return categories?.includes('Zone Reference Images')
    })

    const isZoneRef =
      selectedDocument && zoneRefs.some((doc) => doc.id === selectedDocument.id)

    return {
      zoneReferenceImages: zoneRefs,
      isSelectedDocZoneRef: isZoneRef,
    }
  }, [allDocuments, selectedDocument])

  // Auto-select first reference image if none selected
  const selectedReferenceImage = useMemo(() => {
    if (selectedDocumentId) {
      return zoneReferenceImages.find((doc) => doc.id === selectedDocumentId)
    }
    return zoneReferenceImages[0]
  }, [selectedDocumentId, zoneReferenceImages])

  // Show ALL zones in the list (both drawing-based and asset model zones)
  const allZones = zones

  // Filter zones for the currently selected reference image (for visual overlay)
  const zonesForSelectedImage = useMemo(() => {
    if (!selectedReferenceImage) return []
    return zones.filter(
      (zone) => zone.zone_reference_image_id === selectedReferenceImage.id
    )
  }, [zones, selectedReferenceImage])

  // Create a map of document ID to document name for display
  const documentNamesById = useMemo(() => {
    const map = new Map<string, string>()
    zoneReferenceImages.forEach((doc) => {
      const filename = doc.raw.data.filename as string | undefined
      map.set(
        doc.id,
        doc.data.visibleName || filename || doc.id.substring(0, 8)
      )
    })
    return map
  }, [zoneReferenceImages])

  const handleZoneClick = useCallback(
    (zoneId: string) => {
      const zone = zones.find((z) => z.id === zoneId)
      if (zone?.zone_reference_image_id) {
        setSelectedDocumentId(zone.zone_reference_image_id)
        setSelectedZoneId(zoneId)
      }
    },
    [zones, setSelectedDocumentId]
  )

  const handleZoneDrawn = useCallback(
    (rect: {
      x_start: number
      y_start: number
      x_end: number
      y_end: number
    }) => {
      if (!selectedDocument) return

      let updatedZones: ZoneParams[]
      let targetZoneId: string

      if (redrawingZoneId) {
        // Redraw mode: update the existing zone's drawing_bounds
        updatedZones = zones.map((z) =>
          z.id === redrawingZoneId
            ? {
                ...z,
                drawing_bounds: rect,
                zone_reference_image_id: selectedDocument.id,
              }
            : z
        )
        targetZoneId = redrawingZoneId
        setRedrawingZoneId(undefined)
      } else {
        // New zone mode: create a new zone
        const newZoneColor = ZONE_COLORS[zones.length % ZONE_COLORS.length]
        const newZone: ZoneParams = {
          id: v4().substring(0, 6),
          title: `Zone ${zones.filter((z) => z.zone_reference_image_id === selectedDocument.id).length + 1}`,
          zone_reference_image_id: selectedDocument.id,
          drawing_bounds: rect,
          color: newZoneColor,
          geometry: 'RECTANGLE',
          damage_mechanisms: [],
          zone_recommendations: [],
          assumptions: [],
        }
        updatedZones = [...zones, newZone]
        targetZoneId = newZone.id
      }

      const updatedZonesForImage = updatedZones.filter(
        (z) => z.zone_reference_image_id === selectedDocument.id
      )

      setZones(updatedZones)
      setIsDrawingMode(false)
      setSelectedZoneId(targetZoneId)

      // Update zone drawing mode with the updated zones immediately
      setZoneDrawingMode({
        enabled: true,
        isDrawingMode: false,
        zoneReferenceImageId: selectedDocument.id,
        zones: updatedZonesForImage,
        selectedZoneId: targetZoneId,
        onZoneClick: handleZoneClick,
      })
    },
    [
      setZones,
      selectedDocument,
      zones,
      setZoneDrawingMode,
      handleZoneClick,
      redrawingZoneId,
    ]
  )

  // Update zone drawing mode when selected document changes
  // Enable zone drawing mode whenever a zone reference image is selected
  const handleStartDrawing = useCallback(() => {
    if (!isSelectedDocZoneRef || !selectedDocument) return

    setIsDrawingMode(true)
    setZoneDrawingMode({
      enabled: true,
      isDrawingMode: true,
      zoneReferenceImageId: selectedDocument.id,
      zones: zonesForSelectedImage,
      selectedZoneId,
      onZoneDrawn: handleZoneDrawn,
      onZoneClick: handleZoneClick,
    })
  }, [
    isSelectedDocZoneRef,
    selectedDocument,
    zonesForSelectedImage,
    selectedZoneId,
    setZoneDrawingMode,
    handleZoneDrawn,
    handleZoneClick,
  ])

  const handleCancelDrawing = useCallback(() => {
    setIsDrawingMode(false)
    setRedrawingZoneId(undefined)

    if (selectedDocument && isSelectedDocZoneRef) {
      setZoneDrawingMode({
        enabled: true,
        isDrawingMode: false,
        zoneReferenceImageId: selectedDocument.id,
        zones: zonesForSelectedImage,
        selectedZoneId,
        onZoneClick: handleZoneClick,
      })
    } else {
      setZoneDrawingMode({ enabled: false })
    }
  }, [
    setZoneDrawingMode,
    selectedDocument,
    isSelectedDocZoneRef,
    zonesForSelectedImage,
    selectedZoneId,
    handleZoneClick,
  ])

  const handleRedrawZone = useCallback(
    (zoneId: string) => {
      const zone = zones.find((z) => z.id === zoneId)
      if (!zone) return

      // Navigate to the zone's reference image if it has one
      if (zone.zone_reference_image_id) {
        setSelectedDocumentId(zone.zone_reference_image_id)
      }

      // Clear the existing drawing bounds so the old rectangle disappears
      setZones((prev) =>
        prev.map((z) =>
          z.id === zoneId ? { ...z, drawing_bounds: undefined } : z
        )
      )

      // Set the redrawing zone ID and enter drawing mode
      setRedrawingZoneId(zoneId)
      setSelectedZoneId(zoneId)
      setIsDrawingMode(true)

      // Enter zone drawing mode
      if (zone.zone_reference_image_id) {
        const updatedZonesForImage = zones
          .map((z) =>
            z.id === zoneId ? { ...z, drawing_bounds: undefined } : z
          )
          .filter(
            (z) => z.zone_reference_image_id === zone.zone_reference_image_id
          )

        setZoneDrawingMode({
          enabled: true,
          isDrawingMode: true,
          zoneReferenceImageId: zone.zone_reference_image_id,
          zones: updatedZonesForImage,
          selectedZoneId: zoneId,
          onZoneDrawn: handleZoneDrawn,
          onZoneClick: handleZoneClick,
        })
      }
    },
    [
      zones,
      setSelectedDocumentId,
      setZones,
      setZoneDrawingMode,
      handleZoneDrawn,
      handleZoneClick,
    ]
  )

  const handleDeleteZone = useCallback(
    (zoneId: string) => {
      setZones((prev) => prev.filter((z) => z.id !== zoneId))
      if (selectedZoneId === zoneId) {
        setSelectedZoneId(undefined)
      }
    },
    [setZones, selectedZoneId]
  )

  const handleUpdateZone = useCallback(
    (zoneId: string, updates: Partial<ZoneParams>) => {
      setZones((prev) =>
        prev.map((z) => (z.id === zoneId ? { ...z, ...updates } : z))
      )
    },
    [setZones]
  )

  // The image + dimensions a suggest request was fired FOR, snapshotted at
  // request time. The response handler must convert with these — not with
  // whatever document is selected when the response lands — or a mid-flight
  // document switch would attach the zones to the wrong image and scale them
  // with the wrong natural size.
  const suggestTargetRef = useRef<{
    imageId: string
    naturalSize: { width: number; height: number }
  }>()

  // Apply an assess response: parse the `zones` proposals (normalized bounds)
  // and materialize them as draft zones flagged `ai_suggested` (pending SME
  // review) with the original box frozen in `ai_initial_bounds`. Conversion
  // to pixel drawing_bounds uses the browser-measured natural size.
  const onZoneFieldValues = useCallback(
    (fieldValues: FieldValues, meta: { documentsUsed: string[] }) => {
      const target = suggestTargetRef.current
      if (!target) return
      const proposals = parseZoneProposals(fieldValues)
      if (proposals.length === 0) {
        infoNotification('The AI did not find any zones on this image.')
        return
      }
      // Functional update: the LLM round-trip takes seconds and the editor
      // stays interactive — merging against a closure-captured zones list
      // would silently revert anything drawn/edited while the request flew.
      setZones((prev) => {
        const { keptZones, newZones } = buildDraftZones({
          proposals,
          naturalSize: target.naturalSize,
          imageId: target.imageId,
          existingZones: prev,
        })
        return [...keptZones, ...newZones]
      })
      // Parity with the autofill banner: surface the documents the model drew
      // on so the SME can judge how well-grounded the suggestion is.
      const docs = meta.documentsUsed
      const docsNote = docs.length
        ? ` Informed by ${docs.length} document${docs.length > 1 ? 's' : ''}.`
        : ''
      successNotification(
        `${proposals.length} AI zone${proposals.length > 1 ? 's' : ''} suggested — review and confirm, reject, or edit each.${docsNote}`
      )
    },
    [setZones, successNotification, infoNotification]
  )

  // One access path for all AI fills: the shared assess/section hook, with
  // the reference image passed as a generic runtime input for the
  // zone-suggestion execution strategy.
  const {
    autofill: suggestZones,
    loading: suggesting,
    error: suggestError,
  } = useGongAgentAutofill({
    sectionId: ZONE_ANALYSIS_SECTION_ID,
    onFieldValues: onZoneFieldValues,
    inputs: useMemo(
      () =>
        selectedDocument
          ? { zone_reference_image_id: selectedDocument.id }
          : undefined,
      [selectedDocument]
    ),
  })

  useEffect(() => {
    if (suggestError) {
      errorNotification(`AI zone suggestion failed: ${suggestError}`)
    }
  }, [suggestError, errorNotification])

  // documentId comes from the reporting viewer (paired with the measurement
  // at the source) — never stamped here, where a stale closure across a fast
  // document switch could pair one image's id with another's dimensions.
  const handleImageNaturalSize = useCallback(
    (size: { documentId: string; width: number; height: number }) => {
      setImageNaturalSize(size)
    },
    []
  )

  const handleSuggestZones = useCallback(async () => {
    if (!selectedDocument) return
    if (
      !imageNaturalSize ||
      imageNaturalSize.documentId !== selectedDocument.id
    ) {
      infoNotification(
        'The reference image is still loading — try again in a moment.'
      )
      return
    }
    suggestTargetRef.current = {
      imageId: selectedDocument.id,
      naturalSize: {
        width: imageNaturalSize.width,
        height: imageNaturalSize.height,
      },
    }
    await suggestZones()
  }, [selectedDocument, imageNaturalSize, suggestZones, infoNotification])

  const handleConfirmZone = useCallback(
    (zoneId: string) => {
      setZones((prev) =>
        prev.map((z) => (z.id === zoneId ? { ...z, ai_suggested: false } : z))
      )
    },
    [setZones]
  )

  useEffect(() => {
    if (isSelectedDocZoneRef && selectedDocument && !isCroppingMode) {
      setZoneDrawingMode((prev) => ({
        ...prev,
        enabled: true,
        isDrawingMode: isDrawingMode,
        zoneReferenceImageId: selectedDocument.id,
        zones: zonesForSelectedImage,
        selectedZoneId,
        onZoneDrawn: isDrawingMode ? handleZoneDrawn : undefined,
        onZoneClick: handleZoneClick,
        onImageNaturalSize: handleImageNaturalSize,
      }))
    } else if (!isCroppingMode) {
      setZoneDrawingMode({ enabled: false })
    }
  }, [
    isSelectedDocZoneRef,
    selectedDocument,
    zonesForSelectedImage,
    selectedZoneId,
    isDrawingMode,
    isCroppingMode,
    setZoneDrawingMode,
    handleZoneDrawn,
    handleZoneClick,
    handleImageNaturalSize,
  ])

  // Zone list that always shows (at the bottom)
  // key is needed to prevent unmount when selected document changes
  const zoneListSection = (
    <div key='zone-list'>
      <ZonesHeader>All Defined Zones ({allZones.length})</ZonesHeader>
      {allZones.length === 0 ? (
        <Empty
          description='No zones defined yet. Create a zone reference image and draw zones on it.'
          style={{ padding: '24px' }}
        />
      ) : (
        <ZonesList>
          {allZones.map((zone) => (
            <div
              key={zone.id}
              onClick={() => handleZoneClick(zone.id)}
              style={{ cursor: 'pointer' }}>
              <ZoneRowComponent
                zone={zone}
                totalAssetArea={undefined}
                onDelete={() => handleDeleteZone(zone.id)}
                onUpdate={(updates: Partial<ZoneParams>) =>
                  handleUpdateZone(zone.id, updates)
                }
                onRedraw={() => handleRedrawZone(zone.id)}
                onConfirm={() => handleConfirmZone(zone.id)}
                onReject={() => handleDeleteZone(zone.id)}
                showCoverageBreakdown
              />
              <div
                style={{
                  fontSize: '11px',
                  color: '#666',
                  marginTop: '4px',
                  paddingLeft: '12px',
                }}>
                {zone.zone_reference_image_id ? (
                  <>
                    {documentNamesById.get(zone.zone_reference_image_id) ||
                      'Unknown document'}
                  </>
                ) : zone.componentId ? (
                  <>Asset Model Zone (Component: {zone.componentId})</>
                ) : (
                  <>No source specified</>
                )}
              </div>
            </div>
          ))}
        </ZonesList>
      )}
    </div>
  )

  if (isCroppingMode && selectedDocument) {
    return (
      <Container>
        <CropDocumentPanel
          sourceDocument={selectedDocument}
          unitId={unitId}
          onCropComplete={async (newId) => {
            setIsCroppingMode(false)
            await forceRefresh()
            await new Promise((resolve) => setTimeout(resolve, 100))
            setSelectedDocumentId(newId)
          }}
          onCancel={() => setIsCroppingMode(false)}
        />
      </Container>
    )
  }

  // If selected document is a zone reference image, show zone controls interface
  if (isSelectedDocZoneRef && selectedDocument) {
    return (
      <Container>
        <TopControls>
          <LeftControls>
            <Label>
              Zone Reference Image
              <Tag color='blue' style={{ marginLeft: 8 }}>
                {selectedDocument.data.visibleName || 'Reference Image'}
              </Tag>
            </Label>
          </LeftControls>

          <RightControls>
            <PermissionsGate
              requires={[
                PERMISSION_GECKO_INTERNAL_MEMBER,
                PERMISSION_MODUS,
                ...zoneAgentPermissions,
              ]}>
              <Button
                icon={<Sparkles size={16} />}
                onClick={handleSuggestZones}
                loading={suggesting}
                disabled={isDrawingMode}>
                Suggest zones with AI
              </Button>
            </PermissionsGate>
            <Button
              type={isDrawingMode ? 'default' : 'primary'}
              icon={<Plus size={16} />}
              onClick={handleStartDrawing}
              disabled={isDrawingMode}>
              {isDrawingMode ? 'Draw on Image →' : 'Add Zone'}
            </Button>

            {isDrawingMode && (
              <Button onClick={handleCancelDrawing}>Cancel</Button>
            )}
          </RightControls>
        </TopControls>

        {isDrawingMode && (
          <EmptyStateContainer style={{ padding: '24px' }}>
            <EmptyStateDescription>
              {redrawingZoneId
                ? "Draw a new rectangle on the image to redefine this zone's boundary"
                : 'Draw a rectangle on the image to the left to define a zone'}
            </EmptyStateDescription>
          </EmptyStateContainer>
        )}

        {zoneListSection}
      </Container>
    )
  }

  // If a source document is selected (not a zone ref), show crop option
  if (selectedDocument) {
    const filename = selectedDocument.raw.data.filename as string | undefined
    const docName =
      selectedDocument.data.visibleName || filename || 'this document'

    return (
      <Container>
        <EmptyStateContainer>
          <EmptyStateTitle>Create Zone Reference Image</EmptyStateTitle>
          <EmptyStateDescription>
            Crop a region from the currently selected document ({docName}) to
            create a zone reference image.
          </EmptyStateDescription>
          <Button
            type='primary'
            icon={<Crop size={16} />}
            onClick={() => setIsCroppingMode(true)}>
            Crop Current Document
          </Button>
        </EmptyStateContainer>

        {zoneListSection}
      </Container>
    )
  }

  // No document selected - show instructions
  return (
    <Container>
      <EmptyStateContainer>
        <EmptyStateTitle>No Document Selected</EmptyStateTitle>
        <EmptyStateDescription>
          Select a construction drawing or P&ID from the left sidebar to create
          a zone reference image, or select an existing zone reference image to
          define zones.
        </EmptyStateDescription>
      </EmptyStateContainer>

      {zoneListSection}
    </Container>
  )
}
